import {
  DirectoryCertificatesView,
  onboardingAsMetadataResponseSchema,
  onboardingDcrProbeResponseSchema,
  onboardingCertUploadResponseSchema,
  onboardingCounterpartyContactsResponseSchema,
  onboardingEvidenceRunSchema,
  onboardingInviteOwnerResponseSchema,
  onboardingKcValidateRealmResponseSchema,
  onboardingListCounterpartyContactsResponseSchema,
  onboardingOverviewSchema,
  onboardingP1SmokeResponseSchema,
  onboardingPhaseSchema,
  onboardingPs256GenerateResponseSchema,
  onboardingReviewRequestResponseSchema,
  onboardingSetDirectoryRegistrationResponseSchema,
  onboardingSkipSsoResponseSchema,
  onboardingSsaFetchResponseSchema,
} from '@finnest/power-admin-contracts';
import type {
  DirectoryCertificatesQuery,
  OnboardingAsMetadataInput,
  OnboardingAsMetadataResponse,
  OnboardingDcrProbeInput,
  OnboardingDcrProbeResponse,
  OnboardingCertUploadInput,
  OnboardingCertUploadResponse,
  OnboardingCounterpartyContactsInput,
  OnboardingCounterpartyContactsResponse,
  OnboardingEvidenceRun,
  OnboardingInviteOwnerInput,
  OnboardingInviteOwnerResponse,
  OnboardingKcValidateRealmInput,
  OnboardingKcValidateRealmResponse,
  OnboardingListCounterpartyContactsResponse,
  OnboardingOverview,
  OnboardingP1SmokeInput,
  OnboardingP1SmokeResponse,
  OnboardingReviewRequestInput,
  OnboardingReviewRequestResponse,
  OnboardingPs256GenerateInput,
  OnboardingPs256GenerateResponse,
  OnboardingSetDirectoryRegistrationInput,
  OnboardingSetDirectoryRegistrationResponse,
  OnboardingSkipSsoInput,
  OnboardingSkipSsoResponse,
  OnboardingSsaFetchInput,
  OnboardingSsaFetchResponse,
  Phase,
  PhaseId,
} from '@finnest/power-admin-contracts';

import {
  apiUrl,
  fetcherFor,
  formPostInit,
  getInit,
  jsonPostInit,
  onboardingApiPaths,
  readJsonData,
  withIdempotencyKey,
} from './api-internals.ts';
import type { OnboardingApiOptions } from './api-internals.ts';
import type { OnboardingDirectorySyncEvent } from '../directory-sync/directory-sync-events.ts';

export { streamDirectorySync, triggerDirectorySync } from '../directory-sync/directory-sync-api.ts';
export type { OnboardingDirectorySyncOptions } from '../directory-sync/directory-sync-api.ts';
export type {
  OnboardingApiOptions,
  OnboardingEvidenceRun,
  OnboardingReviewRequestInput,
  OnboardingReviewRequestResponse,
};
export type { OnboardingDirectorySyncEvent };

export type OnboardingCertUploadFormInput = Omit<
  OnboardingCertUploadInput,
  'transport' | 'signing'
> & {
  readonly transport: Blob;
  readonly signing: Blob;
};

const certUploadForm = (input: OnboardingCertUploadFormInput): FormData => {
  const body = new FormData();
  body.set('customerId', input.customerId);
  body.set('certificateKind', input.certificateKind);
  body.set('transport', input.transport);
  body.set('signing', input.signing);
  return body;
};

export const fetchEvidenceRun = async (
  customerId: string,
  phaseDir: string,
  runId: string,
  options: OnboardingApiOptions = {},
): Promise<OnboardingEvidenceRun> => {
  const params = new URLSearchParams({ customerId });
  const path = `${onboardingApiPaths.evidenceRun(phaseDir, runId)}?${params.toString()}`;
  const response = await fetcherFor(options)(apiUrl(path, options), getInit(options));
  return await readJsonData(response, onboardingEvidenceRunSchema);
};

export const fetchOnboardingOverview = async (
  customerId: string,
  options: OnboardingApiOptions = {},
): Promise<OnboardingOverview> => {
  const params = new URLSearchParams({ customerId });
  const response = await fetcherFor(options)(
    apiUrl(`${onboardingApiPaths.overview}?${params.toString()}`, options),
    getInit(options),
  );
  return await readJsonData(response, onboardingOverviewSchema);
};

export const fetchOnboardingPhase = async (
  customerId: string,
  phaseId: PhaseId,
  options: OnboardingApiOptions = {},
): Promise<Phase> => {
  const params = new URLSearchParams({ customerId });
  const path = `${onboardingApiPaths.phase(phaseId)}?${params.toString()}`;
  const response = await fetcherFor(options)(apiUrl(path, options), getInit(options));
  return await readJsonData(response, onboardingPhaseSchema);
};

export const inviteOwner = async (
  input: OnboardingInviteOwnerInput,
  options: OnboardingApiOptions = {},
): Promise<OnboardingInviteOwnerResponse> => {
  const response = await fetcherFor(options)(
    apiUrl(onboardingApiPaths.inviteOwner, options),
    jsonPostInit(input, options),
  );
  return await readJsonData(response, onboardingInviteOwnerResponseSchema);
};

export const skipSso = async (
  input: OnboardingSkipSsoInput,
  options: OnboardingApiOptions = {},
): Promise<OnboardingSkipSsoResponse> => {
  const response = await fetcherFor(options)(
    apiUrl(onboardingApiPaths.skipSso, options),
    jsonPostInit(input, options),
  );
  return await readJsonData(response, onboardingSkipSsoResponseSchema);
};

export const uploadIcpCert = async (
  input: OnboardingCertUploadFormInput,
  options: OnboardingApiOptions = {},
): Promise<OnboardingCertUploadResponse> => {
  const response = await fetcherFor(options)(
    apiUrl(onboardingApiPaths.certUpload, options),
    formPostInit(certUploadForm(input), options),
  );
  return await readJsonData(response, onboardingCertUploadResponseSchema);
};

export const generatePs256 = async (
  input: OnboardingPs256GenerateInput,
  options: OnboardingApiOptions = {},
): Promise<OnboardingPs256GenerateResponse> => {
  const response = await fetcherFor(options)(
    apiUrl(onboardingApiPaths.ps256Generate, options),
    jsonPostInit(input, options),
  );
  return await readJsonData(response, onboardingPs256GenerateResponseSchema);
};

export const fetchSsa = async (
  input: OnboardingSsaFetchInput,
  options: OnboardingApiOptions = {},
): Promise<OnboardingSsaFetchResponse> => {
  const response = await fetcherFor(options)(
    apiUrl(onboardingApiPaths.ssaFetch, options),
    jsonPostInit(input, options),
  );
  return await readJsonData(response, onboardingSsaFetchResponseSchema);
};

export const setDirectoryRegistration = async (
  input: OnboardingSetDirectoryRegistrationInput,
  options: OnboardingApiOptions = {},
): Promise<OnboardingSetDirectoryRegistrationResponse> => {
  const response = await fetcherFor(options)(
    apiUrl(onboardingApiPaths.directoryRegistration, options),
    jsonPostInit(input, options),
  );
  return await readJsonData(response, onboardingSetDirectoryRegistrationResponseSchema);
};

export const runKcValidate = async (
  input: OnboardingKcValidateRealmInput,
  options: OnboardingApiOptions = {},
): Promise<OnboardingKcValidateRealmResponse> => {
  const response = await fetcherFor(options)(
    apiUrl(onboardingApiPaths.kcValidateRealm, options),
    jsonPostInit(input, options),
  );
  return await readJsonData(response, onboardingKcValidateRealmResponseSchema);
};

export const runAsMetadata = async (
  input: OnboardingAsMetadataInput,
  options: OnboardingApiOptions = {},
): Promise<OnboardingAsMetadataResponse> => {
  const response = await fetcherFor(options)(
    apiUrl(onboardingApiPaths.asMetadata, options),
    jsonPostInit(input, options),
  );
  return await readJsonData(response, onboardingAsMetadataResponseSchema);
};

export const runDcrProbe = async (
  input: OnboardingDcrProbeInput,
  options: OnboardingApiOptions = {},
): Promise<OnboardingDcrProbeResponse> => {
  const response = await fetcherFor(options)(
    apiUrl(onboardingApiPaths.dcrProbe, options),
    jsonPostInit(input, options),
  );
  return await readJsonData(response, onboardingDcrProbeResponseSchema);
};

export const runP1Smoke = async (
  input: OnboardingP1SmokeInput,
  options: OnboardingApiOptions = {},
): Promise<OnboardingP1SmokeResponse> => {
  const response = await fetcherFor(options)(
    apiUrl(onboardingApiPaths.p1Smoke, options),
    jsonPostInit(input, options),
  );
  return await readJsonData(response, onboardingP1SmokeResponseSchema);
};

export const upsertCounterpartyContacts = async (
  input: OnboardingCounterpartyContactsInput,
  options: OnboardingApiOptions = {},
): Promise<OnboardingCounterpartyContactsResponse> => {
  const response = await fetcherFor(options)(
    apiUrl(onboardingApiPaths.counterpartyContacts, options),
    jsonPostInit(input, options),
  );
  return await readJsonData(response, onboardingCounterpartyContactsResponseSchema);
};

/** `idempotencyKey` names this one logical request; repeat it on every retry of the request. */
export const requestReview = async (
  input: OnboardingReviewRequestInput,
  idempotencyKey: string,
  options: OnboardingApiOptions = {},
): Promise<OnboardingReviewRequestResponse> => {
  const response = await fetcherFor(options)(
    apiUrl(onboardingApiPaths.requestReview, options),
    withIdempotencyKey(jsonPostInit(input, options), idempotencyKey),
  );
  return await readJsonData(response, onboardingReviewRequestResponseSchema);
};

export const fetchCounterpartyContacts = async (
  customerId: string,
  options: OnboardingApiOptions = {},
): Promise<OnboardingListCounterpartyContactsResponse> => {
  const params = new URLSearchParams({ customerId });
  const path = `${onboardingApiPaths.counterpartyContactsList}?${params.toString()}`;
  const response = await fetcherFor(options)(apiUrl(path, options), getInit(options));
  return await readJsonData(response, onboardingListCounterpartyContactsResponseSchema);
};

/** The certificates the customer's active Directory publishes, with their expiry. */
export const readDirectoryCertificates = async (
  query: DirectoryCertificatesQuery,
  options: OnboardingApiOptions = {},
): Promise<DirectoryCertificatesView> => {
  const params = new URLSearchParams({ customerId: query.customerId, ecosystem: query.ecosystem });
  const path = `${onboardingApiPaths.directoryCertificates}?${params.toString()}`;
  const response = await fetcherFor(options)(apiUrl(path, options), getInit(options));
  return await readJsonData(response, DirectoryCertificatesView);
};
